import { useEffect, useRef } from "react";
import { useQueryClient, type Query } from "@tanstack/react-query";
import {
  getGetDomainQueryKey,
  getGetDomainPagesQueryKey,
  getGetDomainClustersQueryKey,
  getGetDomainStatsQueryKey,
  getGetRadialClustersQueryKey,
} from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";

const ACTIVE_STATES = new Set(["crawling", "linking", "clustering", "analyzing"]);
const TERMINAL_STATES = new Set(["done", "error"]);

export function isActiveCrawlStatus(status: string | undefined | null): boolean {
  return !!status && ACTIVE_STATES.has(status);
}

/**
 * `useGetDomain`'in `refetchInterval` opsiyonuna doğrudan verilebilen helper.
 * Aktif statülerde 3 sn, diğerlerinde polling kapalı.
 */
export function crawlStatusRefetchInterval(
  query: Query<unknown, unknown, unknown, readonly unknown[]>,
): number | false {
  const status = (query.state.data as { status?: string } | undefined)?.status;
  return isActiveCrawlStatus(status) ? 3000 : false;
}

export interface CrawlCompletionInput {
  domainId: number;
  status: string | undefined;
  domainUrl?: string | null;
  totalPages?: number | null;
  averageScore?: number | null;
}

/**
 * Domain durum geçişlerini izler:
 * - Aktif (crawling/linking/clustering/analyzing) → Terminal (done/error) geçişinde
 *   tek seferlik toast gönderir ve ilgili tüm query'leri invalidate eder.
 * - `domainId` değişimini de izler: yeni domain için geçmiş status sıfırlanır
 *   (önceki domain'in state'i ile cross-fire engellenir).
 */
export function useCrawlCompletionToast(input: CrawlCompletionInput): void {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const prevStatusRef = useRef<string | undefined>(undefined);
  const prevDomainIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    // Domain değiştiyse hafızayı sıfırla — hiçbir şey karşılaştırma
    if (prevDomainIdRef.current !== input.domainId) {
      prevDomainIdRef.current = input.domainId;
      prevStatusRef.current = input.status;
      return;
    }

    const prev = prevStatusRef.current;
    const cur = input.status;

    if (cur && prev && prev !== cur && ACTIVE_STATES.has(prev) && TERMINAL_STATES.has(cur)) {
      if (cur === "done") {
        toast({
          title: "Tarama tamamlandı",
          description: `${input.domainUrl ?? "Domain"} başarıyla analiz edildi${
            input.totalPages ? ` — ${input.totalPages} sayfa` : ""
          }${input.averageScore != null ? `, ort. skor: ${input.averageScore}` : ""}.`,
          duration: 8000,
        });
      } else {
        toast({
          title: "Tarama hata ile sonlandı",
          description: `${input.domainUrl ?? "Domain"} tamamlanamadı. Detaylar için domain sayfasına bakın.`,
          variant: "destructive",
          duration: 10000,
        });
      }

      queryClient.invalidateQueries({ queryKey: getGetDomainQueryKey(input.domainId) });
      queryClient.invalidateQueries({ queryKey: getGetDomainPagesQueryKey(input.domainId) });
      queryClient.invalidateQueries({ queryKey: getGetDomainClustersQueryKey(input.domainId) });
      queryClient.invalidateQueries({ queryKey: getGetDomainStatsQueryKey(input.domainId) });
      queryClient.invalidateQueries({ queryKey: getGetRadialClustersQueryKey(input.domainId) });
    }

    prevStatusRef.current = cur;
  }, [input.status, input.domainId, input.domainUrl, input.totalPages, input.averageScore, toast, queryClient]);
}
